import { Entry } from 'contentful'
import { parseFeature } from './parseFeature'
import { IContentfulFeaturesList } from '../../../../types/contentful/featuresList'
import { mapContentfulImageAsset } from '@utils/contentful/contentfulImage'
import { BaseTemplate } from 'types/templates'
import { PostOrPage } from '@tryghost/content-api'
import { getPosts } from '@utils/ghost'
import { IConfigurableFeature } from 'app/_components/ContentfulPage/ConfigurableFeatures/features.types'
import { getRecommendationsForCategory } from '@utils/platform/getRecommendationsForCrossSell'
import { getProductCategoryIdInBigcommerce } from '@framework/lib/getProductCategoryInBigcommerce'
import { IContentfulComponentsParsersData } from '../pageContent'
import { TProductCategoryParentSlug } from 'types/contentful/productCategory'
import { getOverrideProducts } from '@utils/platform/getRecommendationsForCrossSell.utils'
import { TCategory } from 'app/_components/ContentfulPage/ConfigurableList/CategoryCollection'
import { getCategoryCollection } from './getCategoryCollection'
import { parseConfigurableCard } from './parseConfigurableCard'
import { IContentfulConfigurableCard } from 'everest/component/StickySideSlider'
import {
  LIST_VARIANTS,
  IConfigurableList,
  TConfigurableListVariant,
} from 'app/_components/ContentfulPage/ConfigurableList/types'
import { TProductTileData } from 'app/(main)/(catalog)/category/[slug]/_components/ProductTile/ProductTile.types'

export const getWrapPosts = async ({
  wrapBlogTag,
}: {
  wrapBlogTag: string
}): Promise<Partial<PostOrPage>[]> => {
  const THE_WRAP_POSTS_LIMIT = 6
  const THE_WRAP_EXTRA_CONTENT_TAG = 'hash-wrapproducts'

  const theWrapTagPosts = await getPosts({
    limit: THE_WRAP_POSTS_LIMIT,
    tagSlug: `${wrapBlogTag}+custom_excerpt:-null`,
  })

  if (theWrapTagPosts.length >= THE_WRAP_POSTS_LIMIT) {
    return theWrapTagPosts.slice(0, 6)
  } else {
    const fillerPosts = await getPosts({
      limit: THE_WRAP_POSTS_LIMIT - theWrapTagPosts?.length,
      tagSlug: `${THE_WRAP_EXTRA_CONTENT_TAG}+custom_excerpt:-null`,
    })

    return (
      [...theWrapTagPosts, ...fillerPosts]?.map((post) => {
        return {
          feature_image: post.feature_image,
          slug: post.slug,
          title: post.title,
          excerpt: post.excerpt,
          updated_at: post.updated_at,
          created_at: post.created_at,
          primary_author: post.primary_author,
        }
      }) || []
    )
  }
}

const getSuggestedProducts = async ({
  productCategorySlug,
  categories,
  parentSlug,
  preview,
}: {
  productCategorySlug: string
  categories: any[]
  parentSlug: TProductCategoryParentSlug
  preview: boolean
}) => {
  const { name: activeCategoryName } = getProductCategoryIdInBigcommerce(
    categories,
    parentSlug,
    productCategorySlug
  )

  const overrideProducts = await getOverrideProducts(
    productCategorySlug,
    preview
  )

  const productRecommendations = await getRecommendationsForCategory({
    category: activeCategoryName || productCategorySlug,
    overrideProducts,
    preview,
  })

  return productRecommendations
}

export const parseFeaturesList = async (
  component: Entry<IContentfulFeaturesList>,
  dataForParsers: IContentfulComponentsParsersData
): Promise<IConfigurableList> => {
  let features: IConfigurableFeature[] | IContentfulConfigurableCard[] | any[] =
    []
  let includedLists: IConfigurableList[] = []
  const productTemplates: BaseTemplate[] = []
  let suggestedProducts: TProductTileData[] = []
  let wrapBlogPosts: Partial<PostOrPage>[]

  const listVariant: TConfigurableListVariant = component?.fields?.listVariant
  const variantBasedDataId: string = component?.fields?.variantBasedDataId

  if (listVariant === LIST_VARIANTS.TILE_LIST) {
    features = component?.fields?.features?.map((featureCard) =>
      parseConfigurableCard(featureCard)
    )
  }

  if (listVariant === LIST_VARIANTS.FEATURES_GRID) {
    features = component?.fields?.features?.map((featureEntry) => {
      return {
        title: featureEntry?.fields?.h1,
        description: featureEntry?.fields?.h2,
        image: mapContentfulImageAsset(
          featureEntry?.fields?.primaryImages?.[0]
        ),
      }
    })
  }

  if (listVariant === LIST_VARIANTS.MULTI_TAB_FEATURES_LIST) {
    const includedListsPromises = component?.fields?.includedLists?.map(
      async (list) => await parseFeaturesList(list, null)
    )
    includedLists = await Promise.all(includedListsPromises)
  }

  if (
    listVariant === LIST_VARIANTS.FEATURES_COLLECTION ||
    listVariant === LIST_VARIANTS.INFINITE_SCROLL_MARQUEUE
  ) {
    features = component?.fields?.features?.map((feature) =>
      parseFeature(feature)
    )
  }

  if (
    listVariant === LIST_VARIANTS.PRODUCTS_SUGGESTIONS &&
    dataForParsers?.categories &&
    dataForParsers?.parentSlug
  ) {
    suggestedProducts = await getSuggestedProducts({
      productCategorySlug: variantBasedDataId,
      categories: dataForParsers.categories,
      parentSlug: dataForParsers.parentSlug,
      preview: dataForParsers?.preview,
    })
  }

  if (listVariant === LIST_VARIANTS.THE_WRAP_POSTS) {
    wrapBlogPosts = await getWrapPosts({ wrapBlogTag: variantBasedDataId })
  }

  let categoryCollection: TCategory[] = []

  if (listVariant === LIST_VARIANTS.CATEGORY_COLLECTION) {
    categoryCollection = await getCategoryCollection({
      contentfulSlug: variantBasedDataId,
      categories: dataForParsers?.categories,
      preview: dataForParsers?.preview,
    })
  }

  const featuresList: IConfigurableList = {
    ...component?.fields,
    id: component?.sys?.id,
    componentKey: 'featuresList',
    topImage: mapContentfulImageAsset(component?.fields?.topImage),
    listVariant: listVariant,
    productTemplates: productTemplates || null,
    wrapBlogPosts: wrapBlogPosts || null,
    suggestedProducts: suggestedProducts || null,
    categoryCollection: categoryCollection || null,
    features: features || null,
    nestedLists: includedLists,
    titleConfig: component?.fields?.titleConfig?.fields,
  }

  return featuresList
}
